import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Typography, TextField, MenuItem, Checkbox, FormControlLabel, Button, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import admissionDefault from '../../../../data/admission/admission.json';

const MotionPaper = motion.create(Paper);
const MotionButton = motion.create(Button);

const backendUrl = process.env.REACT_APP_DATABASEURL;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRe = /^[6-9]\d{9}$/;

const makeCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    for (let i = 0; i < 6; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
};

const labelSx = {
    display: 'block',
    mb: 0.5,
    ml: 0.5,
    fontWeight: 700,
    fontSize: '0.68rem',
    color: '#475569',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
};

const AdmissionForm = ({ data }) => {
    // Subject / state / city option lists + copy come from the dynamic Admission
    // page content, with the bundled JSON as the instant fallback.
    const d = data || admissionDefault.form;
    const subjects = d.subjects || [];
    const stateCities = d.stateCities || {};
    const states = Object.keys(stateCities);
    const headingWords = String(d.heading || 'Submit Your Query').trim().split(' ');
    const headingLast = headingWords.pop();

    const inputStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            background: '#F8FAFC',
            transition: 'all 0.3s ease',
            fontSize: '0.9rem',
            '& fieldset': { border: '1px solid #E2E8F0' },
            '&:hover': {
                background: '#ffffff',
                '& fieldset': { border: '1px solid #CBD5E1' }
            },
            '&.Mui-focused': {
                background: '#ffffff',
                boxShadow: '0 4px 20px rgba(14, 36, 85, 0.08)',
                '& fieldset': { border: '2px solid #0e2455' }
            }
        }
    };

    const [form, setForm] = useState({
        name: '', mobile: '', email: '', subject: '', state: '', city: '', captcha: '', consent: false,
    });
    const [errors, setErrors] = useState({});
    const [captcha, setCaptcha] = useState(makeCaptcha);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'ok'|'err', text }

    const cities = useMemo(() => (form.state && stateCities[form.state]) || [], [form.state, stateCities]);

    const set = (key, value) => {
        setForm((f) => {
            const next = { ...f, [key]: value };
            if (key === 'state') next.city = '';
            return next;
        });
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const refreshCaptcha = () => {
        setCaptcha(makeCaptcha());
        set('captcha', '');
    };

    // Captcha input: cap length to the displayed code, normalise to uppercase, and
    // validate live as the user types (error only once the full length is entered).
    const onCaptcha = (value) => {
        const v = value.slice(0, captcha.length).toUpperCase();
        setForm((f) => ({ ...f, captcha: v }));
        setErrors((e) => ({
            ...e,
            captcha: v.length === captcha.length && v !== captcha ? 'Captcha does not match.' : undefined,
        }));
    };
    const captchaMatched = form.captcha.length === captcha.length && form.captcha === captcha;

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 3) e.name = 'Please enter your full name.';
        if (!mobileRe.test(form.mobile)) e.mobile = 'Enter a valid 10-digit mobile number.';
        if (!emailRe.test(form.email)) e.email = 'Enter a valid email address.';
        if (!form.subject) e.subject = 'Please select a subject.';
        if (!form.state) e.state = 'Please select your state.';
        if (cities.length > 0 && !form.city) e.city = 'Please select your city.';
        if (form.captcha.trim().toUpperCase() !== captcha) e.captcha = 'Captcha does not match.';
        if (!form.consent) e.consent = 'Please accept to continue.';
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setStatus(null);
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        try {
            setLoading(true);
            const res = await axios.post(
                `${backendUrl}api/add_Enquiry_user`,
                {
                    formType: 'Admission',
                    name: form.name.trim(),
                    email: form.email.trim(),
                    mobile_number: form.mobile.trim(),
                    subject: form.subject,
                    state: form.state,
                    city: form.city,
                },
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (res.status === 200 || res.status === 201) {
                setStatus({ type: 'ok', text: 'Thank you! Your admission query has been submitted. Our counselors will reach out shortly.' });
                setForm({ name: '', mobile: '', email: '', subject: '', state: '', city: '', captcha: '', consent: false });
                refreshCaptcha();
            } else {
                setStatus({ type: 'err', text: 'Something went wrong. Please try again.' });
            }
        } catch (err) {
            setStatus({ type: 'err', text: "We couldn't submit your query right now. Please try again later." });
        } finally {
            setLoading(false);
        }
    };

    const errSx = { color: '#dc2626', fontSize: '0.72rem', mt: 0.5, ml: 0.5, display: 'block' };

    return (
        <MotionPaper
            component="form"
            onSubmit={handleSubmit}
            noValidate
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            sx={{
                width: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: '20px',
                boxShadow: '0 16px 44px -28px rgba(14,36,85,0.28)',
                background: '#ffffff',
                border: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0e2455', mb: 1, letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', md: '1.8rem' }, textTransform: 'uppercase' }}>
                {headingWords.join(' ')} <Box component="span" sx={{ color: '#F6872A' }}>{headingLast}</Box>
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3.5, fontWeight: 500 }}>
                {d.subtitle}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                    <Typography component="label" sx={labelSx}>Full Name *</Typography>
                    <TextField fullWidth placeholder="John Doe" variant="outlined" sx={inputStyles} size="small"
                        value={form.name} onChange={(e) => set('name', e.target.value)} error={!!errors.name} />
                    {errors.name && <Typography component="span" sx={errSx}>{errors.name}</Typography>}
                </Box>
                <Box>
                    <Typography component="label" sx={labelSx}>Mobile Number *</Typography>
                    <TextField fullWidth placeholder="9876543210" variant="outlined" sx={inputStyles} size="small"
                        value={form.mobile} inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        onChange={(e) => set('mobile', e.target.value.replace(/\D/g, ''))} error={!!errors.mobile} />
                    {errors.mobile && <Typography component="span" sx={errSx}>{errors.mobile}</Typography>}
                </Box>
                <Box>
                    <Typography component="label" sx={labelSx}>Email ID *</Typography>
                    <TextField fullWidth placeholder="john@example.com" variant="outlined" sx={inputStyles} size="small"
                        value={form.email} onChange={(e) => set('email', e.target.value)} error={!!errors.email} />
                    {errors.email && <Typography component="span" sx={errSx}>{errors.email}</Typography>}
                </Box>
                <Box>
                    <Typography component="label" sx={labelSx}>Subject *</Typography>
                    <TextField select fullWidth variant="outlined" sx={inputStyles} size="small"
                        value={form.subject} onChange={(e) => set('subject', e.target.value)} error={!!errors.subject}>
                        <MenuItem value="" disabled>Select Subject</MenuItem>
                        {subjects.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    {errors.subject && <Typography component="span" sx={errSx}>{errors.subject}</Typography>}
                </Box>
                <Box>
                    <Typography component="label" sx={labelSx}>Select State *</Typography>
                    <TextField select fullWidth variant="outlined" sx={inputStyles} size="small"
                        value={form.state} onChange={(e) => set('state', e.target.value)} error={!!errors.state}>
                        <MenuItem value="" disabled>Select State</MenuItem>
                        {states.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    {errors.state && <Typography component="span" sx={errSx}>{errors.state}</Typography>}
                </Box>
                <Box>
                    <Typography component="label" sx={labelSx}>Select City *</Typography>
                    <TextField select fullWidth variant="outlined" sx={inputStyles} size="small"
                        value={form.city} onChange={(e) => set('city', e.target.value)} disabled={!form.state} error={!!errors.city}>
                        <MenuItem value="" disabled>{form.state ? 'Select City' : 'Select State first'}</MenuItem>
                        {cities.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    {errors.city && <Typography component="span" sx={errSx}>{errors.city}</Typography>}
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography component="label" sx={labelSx}>Captcha *</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box
                            onClick={refreshCaptcha}
                            title="Click to refresh"
                            sx={{ flex: '0 0 auto', minWidth: 130, py: 1, px: 2, textAlign: 'center', fontSize: '18px', letterSpacing: '4px', fontWeight: 'bold', color: '#334155', background: 'linear-gradient(to right, #F1F5F9, #E2E8F0)', border: '1px solid #CBD5E1', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }}
                        >
                            {captcha}
                        </Box>
                        <TextField placeholder="Enter Captcha" variant="outlined" sx={{ flex: 1, ...inputStyles }} size="small"
                            inputProps={{ maxLength: captcha.length }}
                            value={form.captcha} onChange={(e) => onCaptcha(e.target.value)} error={!!errors.captcha} />
                    </Box>
                    {errors.captcha ? (
                        <Typography component="span" sx={errSx}>{errors.captcha}</Typography>
                    ) : captchaMatched ? (
                        <Typography component="span" sx={{ ...errSx, color: '#15803d' }}>✓ Captcha matched</Typography>
                    ) : null}
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#F6872A' } }} />}
                        label={
                            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 500 }}>
                                {d.consent} *
                            </Typography>
                        }
                    />
                    {errors.consent && <Typography component="span" sx={errSx}>{errors.consent}</Typography>}
                </Box>
            </Box>

            <Box sx={{ pt: 2.5 }}>
                <MotionButton
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.01, boxShadow: '0 10px 24px rgba(246, 135, 42, 0.4)' }}
                    whileTap={{ scale: 0.99 }}
                    fullWidth
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(90deg, #F6872A 0%, #FF9F4A 100%)',
                        color: '#fff',
                        py: 1.5,
                        borderRadius: '12px',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        letterSpacing: '1px',
                        border: 'none',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { background: 'linear-gradient(90deg, #E57A25 0%, #FF9F4A 100%)' }
                    }}
                >
                    {loading ? 'Submitting…' : 'Submit Query'}
                </MotionButton>
            </Box>

            {status && (
                <Typography
                    role="status"
                    sx={{ mt: 2, p: 1.5, borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                        color: status.type === 'ok' ? '#166534' : '#b91c1c',
                        background: status.type === 'ok' ? '#dcfce7' : '#fee2e2' }}
                >
                    {status.text}
                </Typography>
            )}
        </MotionPaper>
    );
};

export default AdmissionForm;
